import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { LeadsType } from "app/pages/sales-intelligence/domain/leads/types/lead";
import multiSelectorPanelTrackingService
    from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { exportToCrmToastThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/common";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";


import type { AccountExportPreview } from "../types";
import { getDomainService } from "../utils/getDomainService";
import { getExportHistoryUrlFromError } from "../utils/getExportHistoryUrlFromError";

import { fetchAccountsPreviewAsync, resetAccountsExport, setAccountsExportPhase } from "./action-creators";
import {
    selectAccountsExportCountries,
    selectAccountsExportListId,
    selectAccountsExportMapping,
    selectAccountsIntegration,
    selectAccountsLeadsType,
} from "./selectors";

// Holds the in-flight preview request so a user cancel can abort it.
let previewAbortController: AbortController | null = null;

export const fetchAccountsPreviewThunk =
    (integration: CrmIntegrationProvider, leadsType: LeadsType, domains: readonly string[]) =>
        async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies): Promise<void> => {
            dispatch(fetchAccountsPreviewAsync.request());

            previewAbortController?.abort();
            const controller = new AbortController();
            previewAbortController = controller;

            try {
                const state = getState();
                const countries = selectAccountsExportCountries(state);
                const listId = selectAccountsExportListId(state);

                const preview = await deps.si.api.accountsCrmExport.fetchAccountsExportPreview(
                    { domains, countries, listId },
                    integration,
                    leadsType,
                    { cancellation: controller.signal },
                );

                dispatch(fetchAccountsPreviewAsync.success(preview as AccountExportPreview[]));
            } catch (error) {
                dispatch(fetchAccountsPreviewAsync.failure(error));
                throw error;
            } finally {
                if (previewAbortController === controller) {
                    previewAbortController = null;
                }
            }
        };

export const confirmAccountsCrmPreviewExportThunk =
    () => async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies): Promise<void> => {
        const state = getState();
        const integration = selectAccountsIntegration(state);
        const leadsType = selectAccountsLeadsType(state);
        const countries = selectAccountsExportCountries(state);
        const listId = selectAccountsExportListId(state);
        const accounts = selectAccountsExportMapping(state);

        if (!integration || !leadsType) {
            return;
        }

        dispatch(setAccountsExportPhase({ type: "exportingAccounts", integration }));

        const domainService = getDomainService(integration);

        try {
            const result = await deps.si.api.accountsCrmExport.exportAccountsToCrm(
                { countries, listId, accounts },
                integration,
                leadsType,
            );

            const { processedRecords, exportHistoryUrl } = result;

            dispatch(
                exportToCrmToastThunk(domainService.getSuccessToastTitle(processedRecords, accounts.length), exportHistoryUrl),
            );
            multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                integration,
                "success",
                accounts.length,
                processedRecords,
            );
        } catch (error) {
            dispatch(exportToCrmToastThunk(domainService.getErrorToastTitle(), getExportHistoryUrlFromError(error)));
            multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(integration, "fail", accounts.length, 0);
        } finally {
            dispatch(resetAccountsExport());
        }
    };

export const cancelAccountsExportThunk = () => (dispatch: ThunkDispatchCommon): void => {
    previewAbortController?.abort();
    previewAbortController = null;
    dispatch(resetAccountsExport());
};
